import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Activity,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Clock,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * System Health View - Simple test dashboard
 * Tests organized by app section (Operations → Roles, Team, Assets, etc.)
 */
const SystemHealthView = ({ platformHealth, fetchPlatformHealth }) => {
  const [testResults, setTestResults] = useState({
    roles: []
  });
  const [runningTests, setRunningTests] = useState(false);

  // Define tests structure
  const rolesTests = [
    { id: 'create_role', name: 'Create Role' },
    { id: 'create_role_permission', name: 'Create Role with Permission' },
    { id: 'update_role_name', name: 'Update Role Name' },
    { id: 'update_role_permission', name: 'Update Role Permission' },
    { id: 'delete_role', name: 'Delete Role' }
  ];

  const runHealthCheck = async () => {
    setRunningTests(true);
    toast.info('Running health checks...');
    
    try {
      const response = await fetch(`${API}/health/test-suites/roles`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (response.ok) {
        // Map backend test results to our test structure
        const mappedResults = data.tests.map(test => ({
          name: test.name,
          status: test.status,
          message: test.message,
          error: test.error
        }));
        
        setTestResults({ roles: mappedResults });
        
        const { passed, failed } = data.summary;
        if (failed === 0) {
          toast.success(`✅ All tests passed!`);
        } else {
          toast.error(`${failed} test(s) failed`);
        }
      } else {
        toast.error('Failed to run tests');
      }
    } catch (error) {
      console.error('Error running tests:', error);
      toast.error('Error running tests');
    } finally {
      setRunningTests(false);
    }
  };

  const getStatusIcon = (status) => {
    if (!status || status === 'waiting') {
      return <Clock className="w-4 h-4 text-gray-400" />;
    }
    switch (status) {
      case 'pass':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'fail':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    if (!status || status === 'waiting') {
      return <Badge className="bg-gray-400 text-white">Waiting...</Badge>;
    }
    switch (status) {
      case 'pass':
        return <Badge className="bg-green-600 text-white">Pass</Badge>;
      case 'fail':
        return <Badge className="bg-red-600 text-white">Fail</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-600 text-white">Warning</Badge>;
      default:
        return <Badge className="bg-gray-400 text-white">Waiting...</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
          <p className="text-gray-600 text-sm mt-1">Test application functionality</p>
        </div>
        <Button
          onClick={runHealthCheck}
          disabled={runningTests}
          className="bg-cyan-600 hover:bg-cyan-700"
        >
          {runningTests ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Activity className="w-4 h-4 mr-2" />
              Run Health Check
            </>
          )}
        </Button>
      </div>

      {/* Operations Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Operations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Roles Sub-section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-base">Roles</h3>
            </div>
            
            <div className="space-y-2">
              {rolesTests.map((test) => {
                const result = testResults.roles.find(r => r.name.includes(test.name.split(' ')[0]));
                const status = result?.status || 'waiting';
                
                return (
                  <div
                    key={test.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      status === 'pass' ? 'bg-green-50 border-green-200' :
                      status === 'fail' ? 'bg-red-50 border-red-200' :
                      status === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status)}
                      <div>
                        <p className="font-medium text-sm">{test.name}</p>
                        {result?.message && status !== 'waiting' && (
                          <p className="text-xs text-gray-600 mt-1">{result.message}</p>
                        )}
                      </div>
                    </div>
                    {getStatusBadge(status)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Future sections placeholder */}
          <div className="pt-4 border-t">
            <p className="text-sm text-gray-500 italic">
              More sections coming soon: Team, Assets, Services, Bookings...
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthView;
